import { ModalPortalContext, useModalPortalTarget } from '@amelio/ui-library';
import { QueryClientProvider } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ToastContainer } from 'react-toastify';

import type { SessionUser } from '@/types/session/SessionUser';
import type { ReactNode } from 'react';

import { hasAiHubToken } from '@/api/ai-gateway/aiHub';
import { queryClient } from '@/api/queryClient';
import { userInstanceAtom } from '@/atoms/userDetails';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ModuleDesignDefaultsProvider } from '@/components/ModuleDesignDefaultsProvider';
import { MOCKS_ENABLED } from '@/env';
import { AiResultProvider } from '@/hooks/ai/AiResultProvider';
import { sentryInit, setSentryUser } from '@/plugins/Sentry';

import '@/plugins/i18n';

sentryInit();

interface ModuleProvidersProps {
    children: ReactNode;
    /**
     * Who is reading, as the host names them. Every entry point receives this from
     * `PerformanceShadowHost` and every tree reads it from `userInstanceAtom` -- to tell an
     * administrator from a manager, and to sign a note with its author's name.
     */
    user?: SessionUser;
    /**
     * Where modals render. Required when an entry point is mounted on its own inside a Shadow DOM,
     * so they render -- and receive events -- inside the isolated container instead of document.body.
     * Left out when an enclosing mount already provides one: the target is then inherited rather
     * than overwritten, because a provider writing `null` would hide it.
     */
    portalTarget?: HTMLElement | null;
    /** Where toasts render; same reason as `portalTarget`. */
    toastTarget?: HTMLElement | null;
}

/**
 * What every federated entry point of this module mounts.
 *
 * Each entry point listed under `exposes` in `vite.config.ts` is a React tree of its own, so a
 * provider declared in one reaches nothing in the others. Declaring them here, once, is what keeps
 * the module behaving the same wherever it is embedded -- and what keeps the next entry point from
 * silently missing whatever the last one gained. `entryPoints.parity.test.tsx` holds that line.
 */
export const ModuleProviders = ({
    children,
    user,
    portalTarget,
    toastTarget,
}: ModuleProvidersProps) => {
    const enclosingPortalTarget = useModalPortalTarget();
    const setReader = useSetAtom(userInstanceAtom);

    useEffect(() => {
        if (user) {
            setReader(user);
        }
    }, [user, setReader]);

    useEffect(() => {
        setSentryUser(user);
    }, [user]);

    const toasts = (
        <ToastContainer
            position={'top-right'}
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
        />
    );

    return (
        <ModuleDesignDefaultsProvider>
            <ModalPortalContext.Provider value={portalTarget ?? enclosingPortalTarget}>
                <QueryClientProvider client={queryClient}>
                    <ErrorBoundary
                        onError={(error, errorInfo) => {
                            console.error('[performance-fe] Error caught:', error, errorInfo);
                        }}
                    >
                        {/* One shared AI hub connection for the tree; opens when a token is
                            available, off under mocks. Gated on the token and not on `user`, which
                            is absent in standalone dev. */}
                        <AiResultProvider enabled={!MOCKS_ENABLED && hasAiHubToken()}>
                            {children}
                        </AiResultProvider>
                        {toastTarget ? createPortal(toasts, toastTarget) : toasts}
                    </ErrorBoundary>
                </QueryClientProvider>
            </ModalPortalContext.Provider>
        </ModuleDesignDefaultsProvider>
    );
};
